import React, { useEffect, useState } from "react";
import { LogOut, Loader2, ExternalLink, RefreshCw, Eye, ChevronRight } from "lucide-react";
import {
  adminLogout,
  verifyAdmin,
  fetchContent,
  updateContent,
  resetContent,
  uploadImage,
} from "../api/admin";
import { useContent } from "../context/ContentContext";
import SectionEditor from "../components/admin/SectionEditor";

const SECTIONS = [
  {
    key: "profile",
    label: "About — Profile & Bio",
    type: "object",
    fields: [
      { key: "name", label: "Full Name" },
      { key: "initials", label: "Initials (for monogram)" },
      { key: "role", label: "Role tagline (header)" },
      { key: "grade", label: "Grade / Class" },
      { key: "school", label: "School (footer text)" },
      { key: "email", label: "Email" },
      { key: "linkedin", label: "LinkedIn URL" },
      { key: "neeyosi", label: "NeeYosi URL" },
      { key: "photoUrl", label: "Photo URL (or upload)", type: "image" },
      { key: "headlineEyebrow", label: "Hero eyebrow (small caps line)" },
      { key: "headlineFirst", label: "Hero — First name (display)" },
      { key: "headlineLast", label: "Hero — Last name (italic green)" },
      { key: "tagRole", label: "Hero — Role line" },
      { key: "tagline", label: "Hero — Tagline (one line)", type: "textarea" },
      { key: "bio", label: "Legacy bio paragraphs (only shown if About Blocks below are empty)", type: "stringArray" },
    ],
  },
  {
    key: "heroStats",
    label: "About — Hero Stats (the 4 numbers)",
    type: "array",
    itemFields: [
      { key: "num", label: "Number" },
      { key: "lab", label: "Label" },
    ],
  },
  {
    key: "researchInterests",
    label: "About — Research Interest Tags",
    type: "stringArray",
  },
  {
    key: "aboutBlocks",
    label: "About — Labelled Content Blocks",
    type: "array",
    itemFields: [
      { key: "label", label: "Block label (e.g. Academics)" },
      { key: "text", label: "Block text (HTML <strong>/<em>/<mark> allowed)", type: "textarea" },
    ],
  },
  {
    key: "awards",
    label: "Awards & Scholarships — Honours Timeline",
    type: "array",
    itemFields: [
      { key: "year", label: "Year" },
      { key: "tier", label: "Tier (international/national/state/district/school)" },
      { key: "badge", label: "Badge text" },
      { key: "title", label: "Title" },
      { key: "org", label: "Organisation" },
      { key: "desc", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "universityPrograms",
    label: "Awards & Scholarships — University Programmes",
    type: "array",
    itemFields: [
      { key: "org", label: "Organisation (small caps line)" },
      { key: "badge", label: "Badge text" },
      { key: "title", label: "Title" },
      { key: "sub", label: "Sub-title" },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "tags", label: "Tags", type: "stringArray" },
      { key: "featured", label: "Featured (dark card)", type: "boolean" },
    ],
  },
  {
    key: "standardisedTests",
    label: "Awards & Scholarships — Standardised Tests (SAT/AP table)",
    type: "array",
    itemFields: [
      { key: "exam", label: "Exam" },
      { key: "component", label: "Component" },
      { key: "score", label: "Score" },
      { key: "date", label: "Date" },
      { key: "featured", label: "Featured (bold green)", type: "boolean" },
      { key: "star", label: "Show star", type: "boolean" },
      { key: "upcoming", label: "Upcoming (greyed)", type: "boolean" },
    ],
  },
  {
    key: "grade10",
    label: "Awards & Scholarships — CBSE Grade 10 Scores",
    type: "array",
    itemFields: [
      { key: "subject", label: "Subject" },
      { key: "score", label: "Score" },
      { key: "note", label: "Note (e.g. 'National 1st')" },
      { key: "featured", label: "Featured (green tile)", type: "boolean" },
    ],
  },
  {
    key: "grade11",
    label: "Awards & Scholarships — CBSE Grade 11 Scores",
    type: "array",
    itemFields: [
      { key: "subject", label: "Subject" },
      { key: "score", label: "Score" },
    ],
  },
  {
    key: "featuredInitiative",
    label: "Research — Featured Initiative (NeeYosi card)",
    type: "object",
    fields: [
      { key: "num", label: "Small caps line" },
      { key: "status", label: "Status" },
      { key: "title", label: "Title" },
      { key: "org", label: "Sub-title" },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "tags", label: "Tags", type: "stringArray" },
      { key: "link", label: "Link URL" },
    ],
  },
  {
    key: "ongoingResearch",
    label: "Research — Ongoing Projects",
    type: "array",
    itemFields: [
      { key: "num", label: "Project label" },
      { key: "status", label: "Status" },
      { key: "title", label: "Title" },
      { key: "org", label: "Mentor / Org" },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "tags", label: "Tags", type: "stringArray" },
    ],
  },
  {
    key: "internships",
    label: "Research — Internships & Projects",
    type: "array",
    itemFields: [
      { key: "num", label: "Project label" },
      { key: "status", label: "Status" },
      { key: "title", label: "Title" },
      { key: "org", label: "Mentor / Org" },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "tags", label: "Tags", type: "stringArray" },
    ],
  },
  {
    key: "manuscripts",
    label: "Research — Manuscripts in Preparation",
    type: "array",
    itemFields: [
      { key: "type", label: "Type" },
      { key: "title", label: "Title" },
      { key: "authors", label: "Authors" },
      { key: "venue", label: "Venue / Target" },
    ],
  },
  {
    key: "internshipReports",
    label: "Research — Internship Reports",
    type: "array",
    itemFields: [
      { key: "type", label: "Type" },
      { key: "title", label: "Title" },
      { key: "authors", label: "Authors" },
      { key: "venue", label: "Venue" },
    ],
  },
  {
    key: "leadership",
    label: "Leadership & Activities",
    type: "array",
    itemFields: [
      { key: "category", label: "Category (small caps line)" },
      { key: "title", label: "Title" },
      { key: "sub", label: "Sub-title" },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "tags", label: "Tags", type: "stringArray" },
    ],
  },
  {
    key: "social",
    label: "Social Entrepreneurship — Page Text",
    type: "object",
    fields: [
      { key: "tagline", label: "Top description (under page title)", type: "textarea" },
      { key: "heroHeadline1", label: "Hero headline part 1" },
      { key: "heroHeadline2", label: "Hero headline part 2 (italic)" },
      { key: "heroHeadline3", label: "Hero headline part 3 (second line)" },
      { key: "heroParagraph", label: "Hero paragraph", type: "textarea" },
      { key: "endorsementName", label: "Endorsement — Name & role" },
      { key: "endorsementBody", label: "Endorsement — Body", type: "textarea" },
    ],
  },
  {
    key: "socialImpactStats",
    label: "Social Entrepreneurship — Impact Stats (4 boxes)",
    type: "array",
    itemFields: [
      { key: "num", label: "Number" },
      { key: "lab", label: "Label" },
    ],
  },
  {
    key: "socialFeatures",
    label: "Social Entrepreneurship — Feature Cards (What it does)",
    type: "array",
    itemFields: [
      { key: "icon", label: "Icon (Database / Languages / Zap / Vote / Users)" },
      { key: "title", label: "Title" },
      { key: "desc", label: "Description", type: "textarea" },
    ],
  },
  {
    key: "socialStory",
    label: "Social Entrepreneurship — Story Paragraphs",
    type: "stringArray",
  },
  {
    key: "mediaNational",
    label: "Media Coverage — National Media",
    type: "array",
    itemFields: [
      { key: "publication", label: "Publication" },
      { key: "section", label: "Section" },
      { key: "brand", label: "Brand colour (hex)", type: "color" },
      { key: "headline", label: "Headline" },
      { key: "sub", label: "Sub-line" },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "url", label: "Article URL" },
      { key: "image", label: "Headline image (upload)", type: "image" },
    ],
  },
  {
    key: "mediaRegional",
    label: "Media Coverage — Regional Media",
    type: "array",
    itemFields: [
      { key: "publication", label: "Publication" },
      { key: "section", label: "Section" },
      { key: "brand", label: "Brand colour (hex)", type: "color" },
      { key: "headline", label: "Headline" },
      { key: "sub", label: "Sub-line" },
      { key: "desc", label: "Description", type: "textarea" },
      { key: "url", label: "Article URL" },
      { key: "image", label: "Headline image (upload)", type: "image" },
    ],
  },
  {
    key: "navItems",
    label: "Navigation — Menu Items",
    type: "array",
    itemFields: [
      { key: "id", label: "ID (about/awards/research/leadership/social/media)" },
      { key: "label", label: "Label (shown in menu)" },
    ],
  },
];

const AdminDashboard = ({ onLogout }) => {
  const { refresh } = useContent();
  const [authChecked, setAuthChecked] = useState(false);
  const [activeKey, setActiveKey] = useState("profile");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState("");
  const [error, setError] = useState("");

  // Auth check on mount
  useEffect(() => {
    (async () => {
      const ok = await verifyAdmin();
      if (!ok) {
        onLogout && onLogout();
        return;
      }
      setAuthChecked(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load content for the active section
  useEffect(() => {
    if (!authChecked) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const value = await fetchContent(activeKey);
        if (!cancelled) setData(value);
      } catch (e) {
        if (!cancelled) setError("Failed to load content");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeKey, authChecked]);

  const handleSave = async () => {
    setSaving(true);
    setSavedMsg("");
    setError("");
    try {
      await updateContent(activeKey, data);
      setSavedMsg("Saved ✓");
      refresh();
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (e) {
      setError("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm("Reset this section to the bundled default? This will overwrite your edits for this section.")) return;
    setSaving(true);
    try {
      await resetContent(activeKey);
      const value = await fetchContent(activeKey);
      setData(value);
      refresh();
      setSavedMsg("Reset ✓");
      setTimeout(() => setSavedMsg(""), 2500);
    } catch (e) {
      setError("Reset failed");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    adminLogout();
    onLogout && onLogout();
  };

  const activeSection = SECTIONS.find((s) => s.key === activeKey);

  if (!authChecked) {
    return (
      <div className="admin-shell">
        <div className="admin-loading"><Loader2 className="spin" size={18} /> Checking credentials…</div>
      </div>
    );
  }

  return (
    <div className="admin-shell">
      <aside className="admin-sidebar">
        <div className="admin-sidebar-head">
          <div className="admin-sidebar-title">Portfolio Admin</div>
          <div className="admin-sidebar-sub">Kapil Bhaskar</div>
        </div>
        <nav className="admin-nav">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              className={`admin-nav-link ${activeKey === s.key ? "active" : ""}`}
              onClick={() => setActiveKey(s.key)}
            >
              <span>{s.label}</span>
              <ChevronRight size={14} className="admin-nav-chev" />
            </button>
          ))}
        </nav>
        <div className="admin-sidebar-foot">
          <a className="admin-foot-link" href="/" target="_blank" rel="noopener noreferrer">
            <Eye size={13} /> View site <ExternalLink size={11} />
          </a>
          <button className="admin-foot-link logout" onClick={handleLogout}>
            <LogOut size={13} /> Logout
          </button>
        </div>
      </aside>

      <main className="admin-main">
        <div className="admin-toolbar">
          <div>
            <div className="admin-page-title">{activeSection?.label}</div>
            <div className="admin-page-sub">Key: <code>{activeKey}</code></div>
          </div>
          <div className="admin-toolbar-actions">
            {savedMsg && <span className="admin-saved">{savedMsg}</span>}
            {error && <span className="admin-saved error">{error}</span>}
            <button className="admin-btn-ghost" onClick={handleReset} disabled={saving}>
              <RefreshCw size={13} /> Reset to default
            </button>
            <button className="admin-btn-primary" onClick={handleSave} disabled={saving || loading}>
              {saving ? <><Loader2 size={13} className="spin" /> Saving…</> : "Save changes"}
            </button>
          </div>
        </div>

        <div className="admin-body">
          {loading ? (
            <div className="admin-loading"><Loader2 className="spin" size={18} /> Loading…</div>
          ) : (
            <SectionEditor
              section={activeSection}
              value={data}
              onChange={setData}
              uploadImage={uploadImage}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
